import { useState, useEffect } from "react";
import { NumberParam, useQueryParams } from "use-query-params";
import { IErrorResponse, IProjectRisk, IProjectRiskFormValues, IQueryRequest } from "../interfaces";
import { issuesApi, useGetProjectRisksQuery, useCreateProjectRiskMutation, useDeleteProjectRiskMutation, useUpdateProjectRiskMutation, useCreateProjectIssueFromRiskMutation } from "@/services";
import { RisksTable } from "./RisksTable";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button, Flex } from "antd";
import { AddEditRiskModal } from "./AddEditRiskModal";
import { RiskHistoryModal } from "@/containers/RisksAndIssues/components/RIskHistoryModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { ConfirmModal } from "@/components";
import { useToggle } from "@/hooks";
import { useProjectContext } from "@/providers";
import { useDispatch } from "react-redux";
import { useIsAdmin } from "@/helpers/permissions";
import { handleServerError } from "../helpers/error-handling";

export const RisksTab = ({ publicProjectId, onTotalCountChange }: { publicProjectId: string, onTotalCountChange: (totalCount: number | undefined) => void }) => {
  const [risksQueryParams, setRisksQueryParams] = useQueryParams({
    r_page: NumberParam,
  });
  const [risksQueryRequest, setRisksQueryRequest] = useState<IQueryRequest>();
  const intl = useIntl();
  const { isUserProjectLeader } = useProjectContext();
  const isAdmin = useIsAdmin();
  const canUpdateRisk = isAdmin || isUserProjectLeader();
  const canDeleteRisk = isAdmin || isUserProjectLeader();
  const [riskToUpdate, setRiskToUpdate] = useState<IProjectRisk | null>(null);
  const [riskHistoryToView, setRiskHistoryToView] = useState<string>();
  const [isAddEditRiskModalOpen, setIsAddEditRiskModalOpen] = useState(false);
  const [isRiskHistoryModalOpen, setIsRiskHistoryModalOpen] = useState(false);
  const [pendingRiskIdToDelete, setPendingRiskIdToDelete] = useState<string>();
  const [deleteConfirmationHidden, toggleDeleteConfirmation] = useToggle();
  const [deleteProjectRisk, { isLoading: isDeletingRisk }] = useDeleteProjectRiskMutation();

  const {
    data: { data: risksData, totalCount: risksTotalCount } = {} as {
      data?: IProjectRisk[];
      totalCount?: number;
    },
    isFetching: isFetchingRisks,
    isError: isRisksError,
    refetch
  } = useGetProjectRisksQuery(
    { publicProjectId, queryParameters: risksQueryRequest },
    { skip: !risksQueryRequest }
  );

  useEffect(() => {
    onTotalCountChange(risksTotalCount);
  }, [risksTotalCount, onTotalCountChange]);

  useEffect(() => {
    const queryRequest: IQueryRequest = {
      page: risksQueryParams.r_page,
    };
    setRisksQueryRequest(queryRequest);
  }, [risksQueryParams]);

  useEffect(() => {
    if (!isRisksError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isRisksError]);

  const [createProjectRisk] = useCreateProjectRiskMutation();
  const [updateProjectRiskMutation] = useUpdateProjectRiskMutation();
  const [createProjectIssueFromRisk] = useCreateProjectIssueFromRiskMutation();
  const dispatch = useDispatch();

  function openCreateUpdateModal(projectRisk: IProjectRisk | null) {
    setRiskToUpdate(projectRisk);
    setIsAddEditRiskModalOpen(true);
  }
  
  function closeCreateUpdateModal() {
    setRiskToUpdate(null);
    setIsAddEditRiskModalOpen(false);
  }

  async function createNewProjectRisk(formValues: IProjectRiskFormValues): Promise<boolean> {
    try {
      await createProjectRisk({ publicProjectId, body: formValues })
        .unwrap();
      toast.success(intl.formatMessage({ id: "risksAndIssues.createRiskSuccess" }));
      return true;
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
      return false;
    }
  }

  async function updateProjectRisk({ publicProjectId, publicProjectRiskId, body }: { publicProjectId: string; publicProjectRiskId: string; body: IProjectRiskFormValues }): Promise<boolean> {
    try {
      await updateProjectRiskMutation({ publicProjectId, publicProjectRiskId, body })
        .unwrap();
      toast.success(intl.formatMessage({ id: "risksAndIssues.updateRiskSuccess" }));
      dispatch(issuesApi.util.resetApiState());
      return true;
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
      return false;
    }
  }

  async function handleChangeToIssue() {
    if (!riskToUpdate) {
      return;
    }

    try {
      await createProjectIssueFromRisk({
        publicProjectId,
        publicProjectRiskId: riskToUpdate.projectRiskPublicId,
      }).unwrap();
      toast.success(intl.formatMessage({ id: "risksAndIssues.changeToIssueSuccess" }));
      closeCreateUpdateModal();
      refetch();
      dispatch(issuesApi.util.resetApiState());
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
    }
  }

  const handleUpdateClick = (projectRisk: IProjectRisk) => openCreateUpdateModal(projectRisk);

  const handleViewHistoryClick = (projectRiskId: string) => {
      setIsRiskHistoryModalOpen(true);
      setRiskHistoryToView(projectRiskId);
  }
  
  const closeRiskHistoryModal = () => {
      setIsRiskHistoryModalOpen(false);
      setRiskHistoryToView(undefined);
  }
  
  const handleDeleteClick = (projectRiskId: string) => {
    if (!canDeleteRisk) {
      return;
    }

    setPendingRiskIdToDelete(projectRiskId);
    toggleDeleteConfirmation();
  };

  async function handleDeleteRisk() {
    try {
      await deleteProjectRisk({ publicProjectId, publicProjectRiskId: pendingRiskIdToDelete })
      .unwrap();
      setPendingRiskIdToDelete(undefined);
      toggleDeleteConfirmation();
      toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      refetch();
      dispatch(issuesApi.util.resetApiState());
    } catch (response) {
      handleServerError(response as IErrorResponse, intl);
    }
  };

  return (
    <Flex gap="middle" vertical>
      <Flex justify="flex-end">
        {canUpdateRisk && (
          <Button
            data-test-id="project-add-risk"
            type="primary"
            onClick={() => openCreateUpdateModal(null)}
            icon={<FontAwesomeIcon icon={faCirclePlus} />}
          >
            {intl.formatMessage({ id: "risksAndIssues.createRisk" })}
          </Button>
        )}
      </Flex>
      <AddEditRiskModal
        open={isAddEditRiskModalOpen}
        onClose={closeCreateUpdateModal}
        onOk={async (values) => {
          let isSuccess = false;
          let pageNumber;
          if (!riskToUpdate) {
            isSuccess = await createNewProjectRisk(values);
            pageNumber = 1;
          } else {
            isSuccess = await updateProjectRisk({ publicProjectId, publicProjectRiskId: riskToUpdate.projectRiskPublicId, body: values })
            pageNumber = risksQueryParams.r_page;
          }

          if (isSuccess) {
            closeCreateUpdateModal();
            setRisksQueryParams({ ...risksQueryParams, r_page: pageNumber });
            refetch();
          }
        }}
        onChangeToIssue={handleChangeToIssue}
        projectRisk={riskToUpdate}
      />
    <RiskHistoryModal 
        open={isRiskHistoryModalOpen} 
        onClose={closeRiskHistoryModal} 
        projectRiskId={riskHistoryToView}
    />
    <RisksTable
      data={risksData}
      totalCount={risksTotalCount}
      isFetching={isFetchingRisks}
      canUpdate={canUpdateRisk}
      onUpdate={handleUpdateClick}
      canDelete={canDeleteRisk}
      onDelete={handleDeleteClick}
      onViewHistory={handleViewHistoryClick}
    />
      <ConfirmModal
        loading={isDeletingRisk}
        onSaveChanges={handleDeleteRisk}
        hidden={deleteConfirmationHidden}
        toggle={toggleDeleteConfirmation}
        headerTitleKey="risksAndIssues.deleteRiskModalTitle"
        bodyTextKey="risksAndIssues.deleteRiskModalDescription"
        additionalBodyText={undefined}
        cancelLabel={undefined}
      />
    </Flex>
  );
};
